import {useState} from 'react';
import {useDispatch} from 'react-redux';
import {SHOP_CHEST_CONFIGS} from '../../configs/chests';
import {calcChestsTotalPrice} from '../../utils/price/calcChestsTotalPrice';
import {updatePlayerRewards} from '../../redux/actions';
import {API_BASE} from '../../utils/apiConfig';

/**
 * Покупка сундука (нумены или кристаллы, с учетом оптовой скидки) — общая
 * логика для магазина (ShopPage) и карусели выбора фракции (FactionSelectPage,
 * кнопка «Получить» для нереализованных фракций открывает Сундук Вечности).
 * onPurchased(chestId, quantity) — опциональный колбэк для локального учета
 * купленных сундуков вызывающей стороной (в ShopPage — обновление грид-беджей).
 */
export function useChestPurchase(auth, onPurchased) {
  const dispatch = useDispatch();
  const [notEnoughCrystalsAmount, setNotEnoughCrystalsAmount] = useState(null);
  const [missingNumensAmount, setMissingNumensAmount] = useState(null);

  const handleBuyChest = async (chestId, quantity) => {
    const chest = SHOP_CHEST_CONFIGS.find((c) => c.id === chestId);
    if (!chest) throw new Error('Chest not found');

    // Премиум-скидка на сундуки за кристаллы — тот же расчет, что на сервере
    const isPremiumActive = Boolean(auth.premiumExpiresAt)
      && new Date(auth.premiumExpiresAt).getTime() > Date.now();
    const {totalPrice, isNumensPrice} = calcChestsTotalPrice(chest, quantity, {isPremiumActive});
    const balance = isNumensPrice ? (auth.numens || 0) : (auth.crystals || 0);

    try {
      if (balance < totalPrice) {
        if (isNumensPrice) {
          // Модалка обмена кристаллов на нумены вместо alert — предзаполнена
          // под недостающую сумму. Ошибка пробрасывается «тихой», чтобы
          // модалка сундука осталась открытой без alert поверх
          setMissingNumensAmount(totalPrice - balance);
          const silentError = new Error('Недостаточно нуменов');
          silentError.isSilent = true;
          throw silentError;
        }
        // Красивая модалка вместо alert: сколько кристаллов не хватает +
        // кнопка перехода во вкладку их покупки. Ошибка пробрасывается
        // «тихой», чтобы модалка сундука осталась открытой без alert
        setNotEnoughCrystalsAmount(totalPrice - balance);
        const silentError = new Error('Недостаточно кристаллов');
        silentError.isSilent = true;
        throw silentError;
      }

      const response = await fetch(`${API_BASE}/buy-chest`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({chestId, quantity}),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to buy chest');
      }

      if (isNumensPrice) {
        dispatch(updatePlayerRewards(-totalPrice, undefined, true));
      } else {
        dispatch(updatePlayerRewards(0, undefined, true, -totalPrice));
      }
      onPurchased?.(chestId, quantity);
    } catch (e) {
      console.error('Buy chest error', e);
      if (!e.isSilent) {
        alert(e.message || 'Не удалось купить сундук');
      }
      throw e;
    }
  };

  return {
    handleBuyChest,
    notEnoughCrystalsAmount,
    setNotEnoughCrystalsAmount,
    missingNumensAmount,
    setMissingNumensAmount,
  };
}
